import { useState } from "react";

import { AddRunner } from "./AddRunner";
import { RunnerPanel } from "./RunnerPanel";
import styles from "./Runners.module.css";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { EmptyState } from "../../components/EmptyState";
import { Pill } from "../../components/Pill";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import {
  drainRunner,
  removeRunner,
  restartRunner,
  resumeRunner,
  RUNNER_STATUS_META,
  runnerSlots,
  useRunners,
} from "../../data/api/runners";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

const Meter = ({ value }: { readonly value: number | null }) => {
  if (value === null) {
    return (
      <span className={styles.meter}>
        <span className={styles.meterNa}>—</span>
      </span>
    );
  }
  const tone =
    value >= 85 ? styles.tHot : value >= 60 ? styles.tWarm : styles.tCool;
  return (
    <span className={styles.meter}>
      <span className={styles.meterTrack}>
        <i
          className={cx(styles.meterFill, tone)}
          style={{ width: `${value}%` }}
        />
      </span>
      <span className={styles.meterVal}>{value}%</span>
    </span>
  );
};

export const RunnersView = () => {
  const { error, loading, refetch, runners, summary } = useRunners();
  // Every fleet mutation is admin-or-owner server-side (runners.md
  // §Permissions), so a member gets the surface read-only: no enrollment, and
  // no lifecycle, rotate or remove actions in the panel.
  const { isAdmin: canManage } = useCurrentWorkspace();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  useDocumentTitle("Runners");

  // Lifecycle actions are optimistic-then-refetch: fire the request and re-sync
  // with server truth regardless of outcome (a role that changed under an open
  // page still gets a 403, which the refetch simply ignores).
  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn();
    } catch {
      // Swallow; refetch reconciles.
    } finally {
      refetch();
    }
  };
  const drain = (id: string): void => void act(() => drainRunner(id));
  const resume = (id: string): void => void act(() => resumeRunner(id));
  const restart = (id: string): void => void act(() => restartRunner(id));
  const remove = (id: string): void => {
    setSelected(null);
    void act(() => removeRunner(id));
  };

  const sel = runners.find((r) => r.id === selected) ?? null;

  const header = (
    <div className={styles.header}>
      <h1 className={styles.title}>Runners</h1>
      <div className={styles.headerRow}>
        <Button onClick={refetch} size="sm">
          <Icon.Refresh size={13} /> Refresh
        </Button>
        {canManage ? (
          <Button onClick={() => setAdding(true)} size="sm" variant="accent">
            <Icon.Plus size={13} /> Add runner
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      {header}

      <div className={styles.summary}>
        <div className={styles.stat}>
          <div className={styles.num}>
            {summary.online}
            <span className={styles.of}>/{summary.total}</span>
          </div>
          <div className={styles.lbl}>Online</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.num}>{summary.agentsRunning}</div>
          <div className={styles.lbl}>Agents running</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.num}>{summary.vcpusAvailable}</div>
          <div className={styles.lbl}>vCPU available</div>
        </div>
      </div>

      {error && runners.length === 0 ? (
        <EmptyState
          action={
            <Button onClick={refetch} size="sm">
              <Icon.Refresh size={13} /> Try again
            </Button>
          }
          body="We couldn't reach the server to load the runner fleet."
          title="Something went sideways."
        />
      ) : !loading && runners.length === 0 ? (
        <EmptyState
          action={
            canManage ? (
              <Button
                onClick={() => setAdding(true)}
                size="sm"
                variant="accent"
              >
                <Icon.Plus size={13} /> Add a runner
              </Button>
            ) : undefined
          }
          body={
            canManage
              ? "Enroll a sandbox host and Tinsmith can build devbox images and run agents on it."
              : "An admin can enroll a sandbox host, and Tinsmith will build devbox images and run agents on it."
          }
          title="No runners yet."
        />
      ) : (
        <div className={styles.table}>
          <div className={cx(styles.row, styles.rowHead)}>
            <span>Runner</span>
            <span>Status</span>
            <span>Agents</span>
            <span>CPU</span>
            <span>Memory</span>
            <span>Disk</span>
            <span>Uptime</span>
            <span />
          </div>
          {loading && runners.length === 0
            ? [0, 1, 2].map((i) => <SkeletonRow key={i} />)
            : runners.map((r) => {
                const meta = RUNNER_STATUS_META[r.status];
                const dim =
                  r.status === "offline" || r.status === "unconnected";
                return (
                  <div
                    className={cx(
                      styles.row,
                      dim && styles.isOffline,
                      r.id === selected && styles.isSelected,
                    )}
                    key={r.id}
                    onClick={() => setSelected(r.id)}
                    role="button"
                    tabIndex={-1}
                  >
                    <div className={styles.nameCell}>
                      <span className={styles.ico}>
                        <Icon.Cpu size={15} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className={styles.name}>{r.name}</div>
                        <div className={styles.sub}>
                          {r.vcpu} vCPU · {r.memoryGb} GB
                        </div>
                      </div>
                      {r.cloud ? (
                        <Chip className={styles.cloudChip}>
                          Cloud · {r.cloud.size}
                        </Chip>
                      ) : null}
                    </div>
                    <Pill
                      className={cx(
                        styles.statusPill,
                        !meta.pill && styles.off,
                      )}
                      dotColor={meta.dotColor}
                      status={meta.pill}
                    >
                      {meta.label}
                    </Pill>
                    <span className={styles.agents}>{runnerSlots(r)}</span>
                    <Meter value={r.cpu} />
                    <Meter value={r.mem} />
                    <Meter value={r.disk} />
                    <span className={styles.uptime}>{r.uptime}</span>
                    <button
                      className={styles.menu}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(r.id);
                      }}
                      title="Manage"
                      type="button"
                    >
                      <Icon.ArrowRight size={15} />
                    </button>
                  </div>
                );
              })}
        </div>
      )}

      {sel ? (
        <RunnerPanel
          canManage={canManage}
          onClose={() => setSelected(null)}
          onDrain={() => drain(sel.id)}
          onRemove={() => remove(sel.id)}
          onRestart={() => restart(sel.id)}
          onResume={() => resume(sel.id)}
          runner={sel}
        />
      ) : null}
      {adding ? (
        <AddRunner onClose={() => setAdding(false)} onEnrolled={refetch} />
      ) : null}
    </div>
  );
};

const SkeletonRow = () => (
  <div className={styles.skelRow}>
    <div className={styles.nameCell}>
      <span className={cx(styles.ico, styles.skelBar)} />
      <div className={styles.skelBar} style={{ width: 140 }} />
    </div>
    <div className={styles.skelBar} style={{ width: 80 }} />
    <div className={styles.skelBar} style={{ width: 24 }} />
    <div className={styles.skelBar} />
    <div className={styles.skelBar} />
    <div className={styles.skelBar} />
    <div className={styles.skelBar} style={{ width: 52 }} />
    <span />
  </div>
);
